import { AjaxStreamCallback, AjaxWrapper, HttpMethod } from 'generic/ajaxWrapper'
import {
  AdminSession,
  RenderSystemPromptResponse,
  SessionHistoryResponse,
  SessionMessage,
} from './types'

export interface ReplayChunk {
  message?: string
  done?: boolean
  error?: string
  [key: string]: any
}

export type ReplayCallback = (chunk: ReplayChunk | null) => void

/**
 * Datasets return `{ payload, statusCode }` where payload is whatever the
 * mysql driver returns from a CALL — typically `[rows, fields]` for an SP,
 * so the actual rows live at `payload[0]`.
 */
const unwrapDatasetRows = <T = any>(response: any): T[] => {
  if (!response) return []
  const payload = response.payload != null ? response.payload : response
  if (Array.isArray(payload) && payload.length > 0 && Array.isArray(payload[0])) {
    return payload[0] as T[]
  }
  if (Array.isArray(payload)) return payload as T[]
  return []
}

export const fetchAdminSessions = (
  communityId: string,
  options: { limit?: number; offset?: number } = {},
): Promise<AdminSession[]> => {
  const params = new URLSearchParams()
  if (options.limit != null) params.set('limit', String(options.limit))
  if (options.offset != null) params.set('offset', String(options.offset))
  const qs = params.toString()
  const url =
    `${AjaxWrapper.getServerUrl()}/dataset/${communityId}/adminAiChatSessions` +
    (qs ? `?${qs}` : '')
  return AjaxWrapper.ajax(url, HttpMethod.GET, {}).then((response: any) =>
    unwrapDatasetRows<AdminSession>(response),
  )
}

const parseHistoryRow = (row: any): SessionMessage => {
  let parsed: any = {}
  try {
    parsed = row.options ? JSON.parse(row.options) : {}
  } catch (_) {
    parsed = {}
  }
  return {
    id: row.id,
    session_id: row.session_id,
    ai_chat_session_id: row.ai_chat_session_id,
    prompt: row.prompt,
    source: row.source,
    ai_prompt_type_id: row.ai_prompt_type_id,
    ai_prompt_template_id: row.ai_prompt_template_id,
    created: row.created,
    updated: row.updated,
    role: parsed.role || 'user',
    chat_type: parsed.chat_type || null,
    options_parsed: parsed,
  }
}

export const fetchSessionHistory = (
  communityId: string,
  sessionId: string,
): Promise<SessionHistoryResponse> => {
  const url = `${AjaxWrapper.getServerUrl()}/dataset/${communityId}/aiChatHistory?sessionId=${encodeURIComponent(sessionId)}`
  return AjaxWrapper.ajax(url, HttpMethod.GET, {}).then((response: any) => {
    const rows = unwrapDatasetRows<any>(response)
    const messages = rows
      .map(parseHistoryRow)
      .sort((a, b) => {
        const ta = new Date(a.created).getTime()
        const tb = new Date(b.created).getTime()
        if (ta !== tb) return ta - tb
        return a.id - b.id
      })
    const firstWithChatType = messages.find((m) => m.chat_type)
    const chatType = firstWithChatType ? firstWithChatType.chat_type : null
    return { sessionId, chatType, messages }
  })
}

export const renderSystemPrompt = (
  communityId: string,
  payload: {
    chatType: string
    sessionId?: string
    sampleUserMessage?: string
    contextOverrides?: Record<string, any>
  },
): Promise<RenderSystemPromptResponse> => {
  const url = `${AjaxWrapper.getServerUrl()}/ai/${communityId}/admin/render-system-prompt`
  return AjaxWrapper.ajax(url, HttpMethod.POST, payload) as Promise<RenderSystemPromptResponse>
}

/**
 * Fire one replay turn. Streams SSE chunks from /ai/:community/chat with
 * dryRun=true so no rows are persisted to ai_chat_history.
 */
export const streamReplayTurn = (
  communityId: string,
  payload: {
    message: string
    sessionId: string
    chatType: string
    systemPromptOverride: string
    conversationHistoryOverride: { role: string; content: string }[]
    extraContext?: Record<string, any>
  },
  callback: ReplayCallback,
) => {
  const url = `${AjaxWrapper.getServerUrl()}/ai/${communityId}/chat`
  const { extraContext, ...rest } = payload
  const body = { ...(extraContext || {}), ...rest, dryRun: true }

  let buffer = ''

  const responseParser: AjaxStreamCallback = (success: boolean, chunk: string) => {
    if (!chunk) {
      callback(null)
      return
    }

    if (!success) {
      callback({ done: true, error: chunk })
      return
    }

    buffer += chunk
    const events = buffer.split('\n\n')
    buffer = events.pop() || ''

    events.forEach((event) => {
      event.split('\n').forEach((line) => {
        if (line.startsWith('data: ')) {
          try {
            const json = line.substring(6)
            callback(JSON.parse(json) as ReplayChunk)
          } catch (_) {
            // ignore parse errors
          }
        }
      })
    })
  }

  AjaxWrapper.stream(url, HttpMethod.POST, body, responseParser)
}
